import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@roger/ui-components';
import {
  FileCheck,
  Pencil,
  Loader2,
  AlertCircle,
  ChevronsUpDown,
  Check,
  ExternalLink,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import type {
  NoaResponse,
  NoaListItem,
  MissingNoaItem,
} from '../services/api/types.gen';
import type { NOAManagerFilters } from '../store/slices/noaSlice';
import {
  listNoasNoasGet,
  getNoaNoasNoaIdGet,
  listMissingNoasNoasMissingGet,
  listNoaPayersNoasPayersGet,
  submitNoa837iNoasNoaIdSubmitPost,
  markPaidNoasNoaIdMarkPaidPost,
  markNotApplicableNoasNoaIdMarkNotApplicablePost,
  markSentNoasNoaIdMarkSentPost,
  createNoaForAdmissionNoasAdmissionsAdmissionIdNoaPost,
} from '../services/api/sdk.gen';
import NOAForm from '../components/NOAForm';
import { TestPatientSubmitConfirmDialog } from '../components/TestPatientSubmitConfirmDialog';
import { isTestPatientAckRequired } from '../utils/apiErrorDetail';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { useToast } from '../hooks/useToastContext';
import { PermissionSlug } from '../constants/featureFlags';
import { ClinicSelect } from '../components/clinic/ClinicSelect';

const STATUS_BY_TAB: Record<string, string> = {
  not_yet_ready: 'not_ready',
  unsent: 'ready_to_send',
  sent: 'sent,paid,accepted,rejected,not_applicable',
};

const DAYS_LEFT_LABELS: Record<string, string> = {
  all: 'All',
  overdue: 'Overdue',
  due_today: 'Due Today',
  due_tomorrow: 'Due Tomorrow',
  due_2_5_days: 'Due in 2-5 days',
};

function daysLeftMatchesFilter(
  daysLeft: number,
  filter: NOAManagerFilters['daysLeftFilter']
): boolean {
  if (filter === 'all') return true;
  if (filter === 'overdue') return daysLeft < 0;
  if (filter === 'due_today') return daysLeft === 0;
  if (filter === 'due_tomorrow') return daysLeft === 1;
  if (filter === 'due_2_5_days') return daysLeft >= 2 && daysLeft <= 5;
  return true;
}

function DaysLeftBadge({ daysLeft }: { daysLeft: number }) {
  if (daysLeft < 0) {
    return (
      <Badge className='bg-red-100 text-red-700 border-red-200'>
        {Math.abs(daysLeft)}d overdue
      </Badge>
    );
  }
  if (daysLeft === 0) {
    return (
      <Badge className='bg-orange-100 text-orange-700 border-orange-200'>
        Due today
      </Badge>
    );
  }
  return (
    <Badge className='bg-orange-50 text-orange-600 border-orange-200'>
      {daysLeft}d left
    </Badge>
  );
}

function StatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    not_ready: 'Not Ready',
    ready_to_send: 'Ready to Send',
    sent: 'Sent',
    paid: 'Paid',
    accepted: 'Accepted',
    rejected: 'Rejected',
    not_applicable: 'N/A',
  };
  const variants: Record<string, string> = {
    accepted: 'text-green-700 border-green-300 bg-green-50',
    rejected: 'text-red-700 border-red-300 bg-red-50',
  };
  return (
    <Badge variant='outline' className={`text-xs ${variants[status] ?? ''}`}>
      {labels[status] ?? status}
    </Badge>
  );
}

function PayerCombobox({
  payers,
  value,
  onChange,
}: {
  payers: { id: string; name: string; noa_count: number }[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = payers.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className='w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-xs text-left hover:bg-accent transition-colors'>
          <span
            className={selected ? 'text-foreground' : 'text-muted-foreground'}
          >
            {selected ? selected.name : 'All payers'}
          </span>
          <ChevronsUpDown className='h-3 w-3 text-muted-foreground shrink-0' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-0' align='start'>
        <Command>
          <CommandInput placeholder='Search payers…' className='text-xs' />
          <CommandList>
            <CommandEmpty className='text-xs py-2 text-center text-muted-foreground'>
              No payers found.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value='all'
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={`mr-2 h-3 w-3 shrink-0 ${value === null ? 'opacity-100' : 'opacity-0'}`}
                />
                <span className='text-xs'>All payers</span>
              </CommandItem>
              {payers.map(p => {
                const hasNoas = p.noa_count > 0;
                return (
                  <CommandItem
                    key={p.id}
                    value={p.name}
                    disabled={!hasNoas}
                    onSelect={() => {
                      if (hasNoas) {
                        onChange(p.id);
                        setOpen(false);
                      }
                    }}
                    className={!hasNoas ? 'opacity-40 cursor-not-allowed' : ''}
                  >
                    <Check
                      className={`mr-2 h-3 w-3 shrink-0 ${value === p.id ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <span className='text-xs flex-1'>{p.name}</span>
                    <span className='text-xs text-muted-foreground ml-1'>
                      ({p.noa_count})
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const NOAManager: React.FC = () => {
  const {
    noaManagerFilters,
    setNOAManagerFilters,
    agencyClinics,
    fetchAgencyClinics,
    hasPermission,
  } = useStore(
    useShallow(state => ({
      noaManagerFilters: state.noaManagerFilters,
      setNOAManagerFilters: state.setNOAManagerFilters,
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
      hasPermission: state.hasPermission,
    }))
  );
  const isInternalAdmin = hasPermission(PermissionSlug.EMR_MANAGE);

  const [noas, setNoas] = useState<NoaListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingNoa, setEditingNoa] = useState<NoaResponse | null>(null);
  const [isBulkActing, setIsBulkActing] = useState(false);
  const [testPatientAckOpen, setTestPatientAckOpen] = useState(false);
  const [pendingTestPatientIds, setPendingTestPatientIds] = useState<string[]>(
    []
  );
  const toast = useToast();

  const [missingNoas, setMissingNoas] = useState<MissingNoaItem[]>([]);
  const [isLoadingMissing, setIsLoadingMissing] = useState(false);
  const [creatingAdmissionId, setCreatingAdmissionId] = useState<string | null>(
    null
  );
  const [payers, setPayers] = useState<
    { id: string; name: string; noa_count: number }[]
  >([]);

  const fetchPayers = useCallback(async () => {
    const tab = noaManagerFilters.activeTab;
    if (tab === 'missing') return;
    const statusParam = STATUS_BY_TAB[tab];
    const resp = await listNoaPayersNoasPayersGet({
      query: {
        status: statusParam || undefined,
        branch_id: noaManagerFilters.branchId || undefined,
        patient_search: noaManagerFilters.patientSearch || undefined,
      },
    });
    if (!resp.error && resp.data) {
      setPayers(resp.data as { id: string; name: string; noa_count: number }[]);
    }
  }, [
    noaManagerFilters.activeTab,
    noaManagerFilters.branchId,
    noaManagerFilters.patientSearch,
  ]);

  useEffect(() => {
    fetchPayers();
  }, [fetchPayers]);

  const [searchInput, setSearchInput] = useState(
    noaManagerFilters.patientSearch
  );
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  const activeTab = noaManagerFilters.activeTab;

  useEffect(() => {
    fetchAgencyClinics();
  }, [fetchAgencyClinics]);

  const fetchMissingNoas = useCallback(async () => {
    setIsLoadingMissing(true);
    try {
      const resp = await listMissingNoasNoasMissingGet({});
      if (!resp.error && resp.data) {
        setMissingNoas(resp.data.items);
        if (resp.data.total === 0 && activeTab === 'missing') {
          setNOAManagerFilters({ activeTab: 'not_yet_ready' });
        }
      }
    } finally {
      setIsLoadingMissing(false);
    }
  }, [activeTab, setNOAManagerFilters]);

  const fetchNoas = useCallback(async () => {
    if (activeTab === 'missing') return;
    setIsLoading(true);
    setSelectedIds(new Set());
    try {
      const statusParam = STATUS_BY_TAB[activeTab];
      const resp = await listNoasNoasGet({
        query: {
          status: statusParam,
          page,
          page_size: 25,
          patient_search: noaManagerFilters.patientSearch || undefined,
          branch_id: noaManagerFilters.branchId || undefined,
          payer_id: noaManagerFilters.payerId || undefined,
        },
      });
      if (!resp.error && resp.data) {
        let filtered = resp.data.items;
        if (noaManagerFilters.daysLeftFilter !== 'all') {
          filtered = filtered.filter(n =>
            daysLeftMatchesFilter(n.days_left, noaManagerFilters.daysLeftFilter)
          );
        }
        if (noaManagerFilters.statusFilter !== 'all') {
          filtered = filtered.filter(
            n => n.status === noaManagerFilters.statusFilter
          );
        }
        if (noaManagerFilters.errorsFilter === 'has_errors') {
          filtered = filtered.filter(n => n.validation_errors.length > 0);
        } else if (noaManagerFilters.errorsFilter === 'no_errors') {
          filtered = filtered.filter(n => n.validation_errors.length === 0);
        }
        setNoas(filtered);
        setTotal(resp.data.total);
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page, noaManagerFilters]);

  useEffect(() => {
    fetchNoas();
  }, [fetchNoas]);

  useEffect(() => {
    fetchMissingNoas();
  }, [fetchMissingNoas]);

  const handlePatientSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setNOAManagerFilters({ patientSearch: value });
      setPage(1);
      searchTimerRef.current = null;
    }, 300);
  };

  const handleTabChange = (tab: string) => {
    setNOAManagerFilters({
      activeTab: tab as NOAManagerFilters['activeTab'],
      statusFilter: 'all',
    });
    setPage(1);
    setSelectedIds(new Set());
  };

  const openEdit = async (id: string) => {
    const resp = await getNoaNoasNoaIdGet({
      path: { noa_id: id },
    });
    if (!resp.error && resp.data) {
      setEditingNoa(resp.data);
    }
  };

  const handleSaved = (updated: NoaResponse) => {
    setNoas(prev =>
      prev.map(n =>
        n.id === updated.id
          ? {
              ...n,
              status: updated.status,
              days_left: updated.days_left,
              is_late: updated.is_late,
              validation_errors: updated.validation_errors,
            }
          : n
      )
    );
    if (editingNoa?.id === updated.id) {
      setEditingNoa(updated);
    }
    // Refresh the list so status changes (e.g. sent) move rows to the correct tab
    fetchNoas();
  };

  const handleCreateNoa = async (admissionId: string) => {
    setCreatingAdmissionId(admissionId);
    try {
      const resp = await createNoaForAdmissionNoasAdmissionsAdmissionIdNoaPost({
        path: { admission_id: admissionId },
      });
      if (!resp.error) {
        // Refresh missing list and switch to not_yet_ready
        await fetchMissingNoas();
        setNOAManagerFilters({ activeTab: 'not_yet_ready' });
        await fetchNoas();
      }
    } finally {
      setCreatingAdmissionId(null);
    }
  };

  // A NOA with outstanding validation errors can't be submitted — the backend
  // rejects it with a 409 — so keep it out of bulk selection even though it's
  // still sitting in the unsent tab.
  const isSubmittable = (n: NoaListItem) =>
    n.status === 'ready_to_send' && n.validation_errors.length === 0;

  // Selection helpers
  const selectableNoas =
    activeTab === 'sent'
      ? noas
      : activeTab === 'unsent'
        ? noas.filter(isSubmittable)
        : [];
  const selectableNoaIds = selectableNoas.map(n => n.id);
  const allSelectableSelected =
    selectableNoaIds.length > 0 &&
    selectableNoaIds.every(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      const next = new Set(selectedIds);
      selectableNoaIds.forEach(id => next.delete(id));
      setSelectedIds(next);
    } else {
      setSelectedIds(new Set(selectableNoaIds));
    }
  };

  const toggleSelect = (id: string, isReady: boolean) => {
    if (!isReady) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const bulkSubmitNoas = (ids: string[], acknowledge: boolean) =>
    Promise.allSettled(
      ids.map(id =>
        submitNoa837iNoasNoaIdSubmitPost({
          path: { noa_id: id },
          query: acknowledge ? { acknowledge_test_patient: true } : undefined,
        })
      )
    );

  const reportBulkResults = (
    count: number,
    results: PromiseSettledResult<{ error?: unknown }>[]
  ) => {
    const failed = results.filter(
      r =>
        r.status === 'rejected' || (r.status === 'fulfilled' && r.value.error)
    );
    const succeeded = count - failed.length;
    if (failed.length === 0) {
      toast.success(
        `Successfully submitted ${succeeded} NOA${succeeded !== 1 ? 's' : ''} to Medicare.`
      );
    } else {
      toast.error(
        `${succeeded} submitted, ${failed.length} failed. Check NOA statuses for details.`
      );
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.size === 0) return;
    setIsBulkActing(true);
    try {
      const ids = Array.from(selectedIds);
      const results = await bulkSubmitNoas(ids, false);
      // Test patients come back as an ack-required 409 — not a failure, a confirm.
      const ackRequiredIds = ids.filter(
        (_, i) =>
          results[i].status === 'fulfilled' &&
          isTestPatientAckRequired(
            (results[i] as PromiseFulfilledResult<{ error?: unknown }>).value
          )
      );
      const settled = ids.filter(id => !ackRequiredIds.includes(id));
      if (settled.length > 0) {
        reportBulkResults(
          settled.length,
          results.filter((_, i) => !ackRequiredIds.includes(ids[i]))
        );
      }
      await fetchNoas();
      if (ackRequiredIds.length > 0) {
        setPendingTestPatientIds(ackRequiredIds);
        setTestPatientAckOpen(true);
      }
    } finally {
      setIsBulkActing(false);
    }
  };

  const confirmTestPatientBulkSubmit = async () => {
    const ackIds = pendingTestPatientIds;
    setTestPatientAckOpen(false);
    setPendingTestPatientIds([]);
    if (ackIds.length === 0) return;
    setIsBulkActing(true);
    try {
      const results = await bulkSubmitNoas(ackIds, true);
      reportBulkResults(ackIds.length, results);
      await fetchNoas();
    } finally {
      setIsBulkActing(false);
    }
  };

  const handleBulkStatusChange = async (
    action:
      | 'mark-paid'
      | 'mark-not-applicable'
      | 'mark-sent'
      | 'mark-not-applicable-na'
  ) => {
    if (selectedIds.size === 0) return;
    setIsBulkActing(true);
    try {
      const sdkFn =
        action === 'mark-paid'
          ? (id: string) =>
              markPaidNoasNoaIdMarkPaidPost({ path: { noa_id: id } })
          : action === 'mark-sent'
            ? (id: string) =>
                markSentNoasNoaIdMarkSentPost({ path: { noa_id: id } })
            : (id: string) =>
                markNotApplicableNoasNoaIdMarkNotApplicablePost({
                  path: { noa_id: id },
                });
      await Promise.all(Array.from(selectedIds).map(sdkFn));
      await fetchNoas();
    } finally {
      setIsBulkActing(false);
    }
  };

  const renderTable = (items: NoaListItem[], showDaysLeft: boolean) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className='w-8'>
            {activeTab !== 'not_yet_ready' && (
              <Checkbox
                checked={allSelectableSelected}
                onCheckedChange={toggleSelectAll}
                checkboxSize='sm'
              />
            )}
          </TableHead>
          {showDaysLeft && <TableHead>Days Left</TableHead>}
          <TableHead>Status</TableHead>
          {showDaysLeft && <TableHead>Errors</TableHead>}
          <TableHead>Subscriber ID</TableHead>
          <TableHead>Patient</TableHead>
          <TableHead>Start of Care</TableHead>
          {!showDaysLeft && <TableHead>Sent Date</TableHead>}
          <TableHead>Branch</TableHead>
          <TableHead>Payer</TableHead>
          <TableHead className='w-8'></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.length === 0 && !isLoading && (
          <TableRow>
            <TableCell
              colSpan={9}
              className='text-center text-muted-foreground py-8'
            >
              No NOAs found.
            </TableCell>
          </TableRow>
        )}
        {items.map(noa => {
          const canSubmit = isSubmittable(noa);
          const isSelected = selectedIds.has(noa.id);
          const socFormatted = noa.soc_date
            ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.soc_date))
            : '—';
          return (
            <TableRow key={noa.id} className={isSelected ? 'bg-accent/20' : ''}>
              <TableCell>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() =>
                    toggleSelect(noa.id, canSubmit || activeTab === 'sent')
                  }
                  disabled={
                    activeTab === 'not_yet_ready' ||
                    (activeTab === 'unsent' && !canSubmit)
                  }
                  checkboxSize='sm'
                />
              </TableCell>
              {showDaysLeft && (
                <TableCell>
                  <DaysLeftBadge daysLeft={noa.days_left} />
                </TableCell>
              )}
              <TableCell>
                <StatusBadge status={noa.status} />
              </TableCell>
              {showDaysLeft && (
                <TableCell>
                  {noa.validation_errors.length > 0 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='text-xs text-red-600 cursor-default underline decoration-dotted'>
                            {noa.validation_errors.length} error
                            {noa.validation_errors.length !== 1 ? 's' : ''}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className='max-w-xs'>
                          <ul className='space-y-0.5'>
                            {noa.validation_errors.map((e, i) => (
                              <li key={i} className='text-xs'>
                                • {e.message}
                              </li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className='text-xs text-green-600'>OK</span>
                  )}
                </TableCell>
              )}
              <TableCell className='text-sm'>
                {noa.subscriber_id || '—'}
              </TableCell>
              <TableCell className='text-sm font-medium'>
                {noa.patient_last_name}, {noa.patient_first_name}
              </TableCell>
              <TableCell className='text-sm'>{socFormatted}</TableCell>
              {!showDaysLeft && (
                <TableCell className='text-sm'>
                  {noa.sent_date
                    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.sent_date))
                    : '—'}
                </TableCell>
              )}
              <TableCell className='text-sm'>
                {noa.branch_name || '—'}
              </TableCell>
              <TableCell className='text-sm'>{noa.payer_name || '—'}</TableCell>
              <TableCell>
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => openEdit(noa.id)}
                    className='text-muted-foreground hover:text-primary'
                  >
                    <Pencil className='h-4 w-4' />
                  </button>
                  {!showDaysLeft && noa.stedi_portal_url && isInternalAdmin && (
                    <a
                      href={noa.stedi_portal_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-muted-foreground hover:text-primary'
                    >
                      <ExternalLink className='h-4 w-4' />
                    </a>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const renderMissingTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Days Left</TableHead>
          <TableHead>Patient</TableHead>
          <TableHead>Start of Care</TableHead>
          <TableHead>Branch</TableHead>
          <TableHead>Payer</TableHead>
          <TableHead className='w-32'></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {missingNoas.length === 0 && !isLoadingMissing && (
          <TableRow>
            <TableCell
              colSpan={6}
              className='text-center text-muted-foreground py-8'
            >
              No missing NOAs.
            </TableCell>
          </TableRow>
        )}
        {missingNoas.map(item => {
          const socFormatted = item.soc_date
            ? formatDateToMMDDYYYY(parseISOToLocalDate(item.soc_date))
            : '—';
          const isCreating = creatingAdmissionId === item.admission_id;
          return (
            <TableRow key={item.admission_id}>
              <TableCell>
                <DaysLeftBadge daysLeft={item.days_left} />
              </TableCell>
              <TableCell className='text-sm font-medium'>
                {item.patient_last_name}, {item.patient_first_name}
              </TableCell>
              <TableCell className='text-sm'>{socFormatted}</TableCell>
              <TableCell className='text-sm'>
                {item.branch_name || '—'}
              </TableCell>
              <TableCell className='text-sm'>
                {item.payer_name || '—'}
              </TableCell>
              <TableCell>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => handleCreateNoa(item.admission_id)}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <Loader2 className='h-3 w-3 animate-spin mr-1' />
                  ) : null}
                  Create NOA
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const showMissingTab = missingNoas.length > 0 || activeTab === 'missing';

  return (
    <div className='flex flex-col md:flex-row h-full'>
      {/* Sidebar filters — stacks above content on mobile (compact 2-col grid) */}
      <aside className='w-full md:w-56 border-b md:border-r md:border-b-0 p-4 shrink-0 grid grid-cols-2 gap-x-3 gap-y-3 md:block md:space-y-4'>
        <h2 className='text-sm font-semibold col-span-2'>Filters</h2>

        {/* Clinic / branch filter */}
        <div className='space-y-1'>
          <ClinicSelect
            label='Branch'
            selectSize='sm'
            clinics={agencyClinics}
            includeAllOption
            allOptionValue='all'
            allOptionLabel='All branches'
            value={noaManagerFilters.branchId ?? 'all'}
            onChange={e => {
              const v = e.target.value;
              setNOAManagerFilters({ branchId: v === 'all' ? null : v });
              setPage(1);
            }}
            placeholder='All branches'
          />
        </div>

        {/* Days left filter */}
        <div className='space-y-1'>
          <label className='text-xs text-muted-foreground'>Days Left</label>
          <Select
            value={noaManagerFilters.daysLeftFilter}
            onValueChange={v =>
              setNOAManagerFilters({
                daysLeftFilter: v as NOAManagerFilters['daysLeftFilter'],
              })
            }
          >
            <SelectTrigger selectSize='sm'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DAYS_LEFT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Patient search */}
        <div className='space-y-1'>
          <label className='text-xs text-muted-foreground'>Patient</label>
          <Input
            value={searchInput}
            onChange={e => handlePatientSearchChange(e.target.value)}
            placeholder='Search patient…'
            inputSize='sm'
          />
        </div>

        {/* Payer filter */}
        <div className='space-y-1'>
          <label className='text-xs text-muted-foreground'>Payer</label>
          <PayerCombobox
            payers={payers}
            value={noaManagerFilters.payerId}
            onChange={v => {
              setNOAManagerFilters({ payerId: v });
              setPage(1);
            }}
          />
        </div>

        {/* Status filter — only meaningful on Sent tab */}
        {activeTab === 'sent' && (
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>Status</label>
            <Select
              value={noaManagerFilters.statusFilter}
              onValueChange={v => setNOAManagerFilters({ statusFilter: v })}
            >
              <SelectTrigger selectSize='sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='sent'>Sent</SelectItem>
                <SelectItem value='accepted'>Accepted</SelectItem>
                <SelectItem value='rejected'>Rejected</SelectItem>
                <SelectItem value='paid'>Paid</SelectItem>
                <SelectItem value='not_applicable'>N/A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Errors filter */}
        {activeTab !== 'sent' && activeTab !== 'missing' && (
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>Errors</label>
            <Select
              value={noaManagerFilters.errorsFilter}
              onValueChange={v =>
                setNOAManagerFilters({
                  errorsFilter: v as NOAManagerFilters['errorsFilter'],
                })
              }
            >
              <SelectTrigger selectSize='sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='has_errors'>Has Errors</SelectItem>
                <SelectItem value='no_errors'>No Errors</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className='flex-1 flex flex-col overflow-hidden'>
        {/* Header */}
        <div className='border-b px-4 py-3 flex items-center gap-2'>
          <FileCheck className='h-5 w-5 text-primary' />
          <h1 className='text-lg font-semibold'>NOA Manager</h1>
          {(isLoading || isLoadingMissing) && (
            <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
          )}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className='flex-1 flex flex-col overflow-hidden'
        >
          <div className='px-4 pt-3 border-b flex items-center justify-between'>
            <TabsList>
              <TabsTrigger value='not_yet_ready'>Not Yet Ready</TabsTrigger>
              <TabsTrigger value='unsent'>Unsent</TabsTrigger>
              <TabsTrigger value='sent'>Sent</TabsTrigger>
              {showMissingTab && (
                <TabsTrigger
                  value='missing'
                  className='flex items-center gap-1'
                >
                  <AlertCircle className='h-3 w-3 text-amber-500' />
                  Missing NOAs
                  <Badge className='ml-1 bg-amber-100 text-amber-700 border-amber-200 text-xs'>
                    {missingNoas.length}
                  </Badge>
                </TabsTrigger>
              )}
            </TabsList>

            {/* Bulk actions */}
            {selectedIds.size > 0 && (
              <div className='flex items-center gap-2'>
                <span className='text-sm text-muted-foreground'>
                  {selectedIds.size} selected
                </span>
                {activeTab === 'unsent' && (
                  <Button
                    size='sm'
                    onClick={handleBulkSubmit}
                    disabled={isBulkActing}
                  >
                    {isBulkActing && (
                      <Loader2 className='h-3 w-3 animate-spin mr-1' />
                    )}
                    Submit to Medicare
                  </Button>
                )}
                {activeTab === 'sent' && (
                  <>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => handleBulkStatusChange('mark-paid')}
                      disabled={isBulkActing}
                    >
                      Mark Paid
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() =>
                        handleBulkStatusChange('mark-not-applicable-na')
                      }
                      disabled={isBulkActing}
                    >
                      Mark N/A
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => handleBulkStatusChange('mark-sent')}
                      disabled={isBulkActing}
                    >
                      Mark Sent
                    </Button>
                  </>
                )}
                {(activeTab === 'not_yet_ready' || activeTab === 'unsent') && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() =>
                      handleBulkStatusChange('mark-not-applicable-na')
                    }
                    disabled={isBulkActing}
                  >
                    Mark N/A
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className='flex-1 overflow-auto'>
            <TabsContent value='not_yet_ready' className='mt-0 h-full'>
              {renderTable(noas, true)}
            </TabsContent>
            <TabsContent value='unsent' className='mt-0 h-full'>
              {renderTable(noas, true)}
            </TabsContent>
            <TabsContent value='sent' className='mt-0 h-full'>
              {renderTable(noas, false)}
            </TabsContent>
            <TabsContent value='missing' className='mt-0 h-full'>
              {renderMissingTable()}
            </TabsContent>
          </div>
        </Tabs>

        {/* Pagination (not shown on missing tab) */}
        {activeTab !== 'missing' && total > 25 && (
          <div className='border-t px-4 py-2 flex items-center justify-between'>
            <span className='text-sm text-muted-foreground'>
              Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of{' '}
              {total}
            </span>
            <div className='flex gap-2'>
              <Button
                size='sm'
                variant='outline'
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => setPage(p => p + 1)}
                disabled={page * 25 >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* NOA Form Sheet */}
      {editingNoa && (
        <NOAForm
          noa={editingNoa}
          open={!!editingNoa}
          onClose={() => setEditingNoa(null)}
          onSaved={handleSaved}
        />
      )}
      <TestPatientSubmitConfirmDialog
        open={testPatientAckOpen}
        actionLabel='Notice of Admission'
        count={pendingTestPatientIds.length}
        loading={isBulkActing}
        onConfirm={() => void confirmTestPatientBulkSubmit()}
        onCancel={() => {
          setTestPatientAckOpen(false);
          setPendingTestPatientIds([]);
        }}
      />
    </div>
  );
};

export default NOAManager;
